import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ExternalLink, FileText, AlertTriangle, CheckCircle2, ChevronRight, ArrowLeft,
  Building2, Truck, Package, User, Container, Plus, Trash2, Search, RotateCcw,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { AddCompanyDocumentDialog } from '@/components/admin/AddCompanyDocumentDialog';
import { COMPANY_DOC_CATEGORIES, COMPANY_DOCS_BUCKET, CompanyDocument } from '@/hooks/useCompanyDocuments';

type Side = 'shipper' | 'transporter';
type StatusFilter = 'all' | 'verified' | 'expiring' | 'expired' | 'unverified';

interface DocItem {
  id: string;
  category: string;
  file_name: string;
  file_size: number;
  file_url: string;
  bucket: string;
  created_at: string;
  status?: 'verified' | 'expiring' | 'expired' | 'unverified';
  title?: string | null;
  notes?: string | null;
  addedByVelox?: boolean;
  companyDoc?: CompanyDocument;
}

interface CompanyNode {
  company_id: string;
  company_name: string;
  side: Side;
  users: { id: string; name: string; email: string }[];
  onboarding: DocItem[];
  fleet: {
    trucks: { id: string; label: string; meta: string; docs: DocItem[]; status: DocItem['status'] }[];
    trailers: { id: string; label: string; meta: string; docs: DocItem[]; status: DocItem['status'] }[];
    drivers: { id: string; label: string; meta: string; docs: DocItem[]; status: DocItem['status'] }[];
  };
  freight: {
    order_id: string;
    order_number: string;
    created_at: string;
    docs: DocItem[];
  }[];
  totals: { docs: number; missing: number; expired: number };
}

const formatSize = (bytes: number) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const extractStoragePath = (url: string, bucket: string) => {
  const marker = `/${bucket}/`;
  const i = url.indexOf(marker);
  if (i === -1) return null;
  return url.substring(i + marker.length).split('?')[0];
};

const computeStatus = (verifiedAt?: string | null, expiry?: string | null): DocItem['status'] => {
  if (!verifiedAt || !expiry) return 'unverified';
  const days = Math.ceil((new Date(expiry).getTime() - Date.now()) / 86400000);
  if (days <= 0) return 'expired';
  if (days <= 30) return 'expiring';
  return 'verified';
};

const expiryStatus = (expiry?: string | null): DocItem['status'] => {
  if (!expiry) return undefined;
  const days = Math.ceil((new Date(expiry).getTime() - Date.now()) / 86400000);
  if (days <= 0) return 'expired';
  if (days <= 30) return 'expiring';
  return 'verified';
};

const StatusBadge = ({ status }: { status?: DocItem['status'] }) => {
  const { t } = useTranslation();
  if (!status) return null;
  const map = {
    verified: { cls: 'bg-[#14A38B]/10 text-[#14A38B] border-[#14A38B]/20', label: t('adminDocuments.statusVerified') },
    expiring: { cls: 'bg-[#F2AC57]/10 text-[#F2AC57] border-[#F2AC57]/20', label: t('adminDocuments.statusExpiring') },
    expired: { cls: 'bg-[#FF7171]/10 text-[#FF7171] border-[#FF7171]/20', label: t('adminDocuments.statusExpired') },
    unverified: { cls: 'bg-[#5A6B7A]/10 text-[#5A6B7A] border-[#5A6B7A]/20', label: t('adminDocuments.statusUnverified') },
  } as const;
  const v = map[status];
  return <Badge className={`${v.cls} font-['Funnel_Sans']`}>{v.label}</Badge>;
};

const useAdminDocsTree = () =>
  useQuery({
    queryKey: ['admin-docs-tree-v3'],
    queryFn: async (): Promise<CompanyNode[]> => {
      const [
        companiesRes, usersRes, vehiclesRes, vehicleDocsRes, driversRes, ordersRes, orderDocsRes, companyDocsRes,
      ] = await Promise.all([
        supabase.from('companies').select('id, name, type'),
        supabase.from('users').select('id, first_name, last_name, email, company_id'),
        supabase.from('vehicles').select(
          'id, plate_number, brand, model, year, company_id, vehicle_type_id, registration_verified_at, registration_expiry'
        ),
        supabase.from('vehicle_documents').select('id, vehicle_id, company_id, document_type, file_name, file_size, file_url, created_at'),
        supabase.from('drivers').select('id, user_id, company_id, license_number, license_expiry, license_verified_at'),
        supabase.from('orders').select(
          'id, order_number, shipper_company_id, created_at, cargo_picture, start_pictures, delivery_pictures, transport_jobs(transporter_company_id)'
        ).order('created_at', { ascending: false }),
        supabase.from('documents').select('id, order_id, file_name, file_size, file_url, created_at'),
        supabase.from('company_documents').select('*').order('created_at', { ascending: false }),
      ]);

      const usersById = new Map<string, any>();
      (usersRes.data || []).forEach((u: any) => usersById.set(u.id, u));

      const tree = new Map<string, CompanyNode>();
      (companiesRes.data || []).forEach((c: any) => {
        if (c.type !== 'shipper' && c.type !== 'transporter') return;
        tree.set(c.id, {
          company_id: c.id, company_name: c.name, side: c.type,
          users: [], onboarding: [],
          fleet: { trucks: [], trailers: [], drivers: [] },
          freight: [],
          totals: { docs: 0, missing: 0, expired: 0 },
        });
      });

      (usersRes.data || []).forEach((u: any) => {
        const node = tree.get(u.company_id);
        if (node) node.users.push({
          id: u.id,
          name: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || '-',
          email: u.email,
        });
      });

      const docsByVehicle = new Map<string, any[]>();
      (vehicleDocsRes.data || []).forEach((d: any) => {
        if (!d.vehicle_id) return;
        const arr = docsByVehicle.get(d.vehicle_id) || [];
        arr.push(d);
        docsByVehicle.set(d.vehicle_id, arr);
      });

      const docTypeCategory = (t: string) =>
        t === 'registration_card' ? 'registration_card'
        : t === 'insurance' ? 'insurance_license'
        : t === 'inspection' ? 'inspection'
        : 'other';

      const toCompanyDocItem = (d: any): DocItem => ({
        id: d.id,
        category: d.category,
        file_name: d.file_name,
        file_size: d.file_size,
        file_url: d.file_url,
        bucket: COMPANY_DOCS_BUCKET,
        created_at: d.created_at,
        status: expiryStatus(d.expiry_date),
        title: d.title,
        notes: d.notes,
        addedByVelox: true,
        companyDoc: d as CompanyDocument,
      });

      const companyDocsByVehicle = new Map<string, DocItem[]>();
      const companyDocsByDriver = new Map<string, DocItem[]>();
      (companyDocsRes.data || []).forEach((d: any) => {
        if (d.vehicle_id) {
          const arr = companyDocsByVehicle.get(d.vehicle_id) || [];
          arr.push(toCompanyDocItem(d));
          companyDocsByVehicle.set(d.vehicle_id, arr);
        } else if (d.driver_id) {
          const arr = companyDocsByDriver.get(d.driver_id) || [];
          arr.push(toCompanyDocItem(d));
          companyDocsByDriver.set(d.driver_id, arr);
        }
      });

      (vehiclesRes.data || []).forEach((v: any) => {
        const node = tree.get(v.company_id);
        if (!node) return;
        const status = computeStatus(v.registration_verified_at, v.registration_expiry);
        const vehicleDocs: DocItem[] = (docsByVehicle.get(v.id) || []).map((d: any) => ({
          id: d.id,
          category: docTypeCategory(d.document_type),
          file_name: d.file_name,
          file_size: d.file_size,
          file_url: d.file_url,
          bucket: 'vehicle-documents',
          created_at: d.created_at,
        }));
        const allVehicleDocs = [...vehicleDocs, ...(companyDocsByVehicle.get(v.id) || [])];
        node.fleet.trucks.push({
          id: v.id,
          label: v.plate_number || '-',
          meta: [v.brand, v.model, v.year].filter(Boolean).join(' ') || '—',
          docs: allVehicleDocs,
          status,
        });
        if (status === 'expired') node.totals.expired++;
        if (status === 'unverified') node.totals.missing++;
        node.totals.docs += allVehicleDocs.length;
      });

      (driversRes.data || []).forEach((d: any) => {
        const node = tree.get(d.company_id);
        if (!node) return;
        const u = usersById.get(d.user_id);
        const status = computeStatus(d.license_verified_at, d.license_expiry);
        const driverDocs = companyDocsByDriver.get(d.id) || [];
        node.fleet.drivers.push({
          id: d.id,
          label: u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email : '—',
          meta: '',
          docs: driverDocs,
          status,
          ...({ _license: d.license_number, _expiry: d.license_expiry } as any),
        } as any);
        node.totals.docs += driverDocs.length;
        if (status === 'expired') node.totals.expired++;
        if (status === 'unverified') node.totals.missing++;
      });

      const docsByOrder = new Map<string, any[]>();
      (orderDocsRes.data || []).forEach((d: any) => {
        const arr = docsByOrder.get(d.order_id) || [];
        arr.push(d);
        docsByOrder.set(d.order_id, arr);
      });

      const companyDocsByOrder = new Map<string, CompanyDocument[]>();
      (companyDocsRes.data || []).forEach((d: any) => {
        if (d.vehicle_id || d.driver_id) return;
        const node = tree.get(d.company_id);
        const item = toCompanyDocItem(d);
        if (d.order_id) {
          const arr = companyDocsByOrder.get(`${d.company_id}:${d.order_id}`) || [];
          arr.push(item as any);
          companyDocsByOrder.set(`${d.company_id}:${d.order_id}`, arr);
        } else if (node) {
          node.onboarding.push(item);
          node.totals.docs++;
          if (item.status === 'expired') node.totals.expired++;
        }
      });

      (ordersRes.data || []).forEach((o: any) => {
        const docs: DocItem[] = [];
        if (o.cargo_picture) docs.push({
          id: `${o.id}-cargo`, category: 'loading_photos',
          file_name: o.cargo_picture.split('/').pop() || 'cargo',
          file_size: 0, file_url: o.cargo_picture, bucket: 'order-images', created_at: o.created_at,
        });
        (o.start_pictures || []).forEach((p: string, i: number) => docs.push({
          id: `${o.id}-start-${i}`, category: 'loading_photos',
          file_name: p.split('/').pop() || `start-${i}`,
          file_size: 0, file_url: p, bucket: 'order-images', created_at: o.created_at,
        }));
        (o.delivery_pictures || []).forEach((p: string, i: number) => docs.push({
          id: `${o.id}-delivery-${i}`, category: 'delivery_photos',
          file_name: p.split('/').pop() || `delivery-${i}`,
          file_size: 0, file_url: p, bucket: 'order-images', created_at: o.created_at,
        }));
        (docsByOrder.get(o.id) || []).forEach((d: any) => docs.push({
          id: d.id, category: 'cmr',
          file_name: d.file_name, file_size: d.file_size,
          file_url: d.file_url, bucket: 'delivery_documents', created_at: d.created_at,
        }));

        const attach = (node: CompanyNode | undefined) => {
          if (!node) return;
          const extra = (companyDocsByOrder.get(`${node.company_id}:${o.id}`) || []) as unknown as DocItem[];
          const all = [...docs, ...extra];
          if (all.length === 0) return;
          node.freight.push({ order_id: o.id, order_number: o.order_number, created_at: o.created_at, docs: all });
          node.totals.docs += all.length;
        };

        attach(tree.get(o.shipper_company_id));
        const jobs = Array.isArray(o.transport_jobs) ? o.transport_jobs : [];
        jobs.forEach((j: any) => attach(tree.get(j.transporter_company_id)));
      });

      return Array.from(tree.values()).sort((a, b) => a.company_name.localeCompare(b.company_name));
    },
  });

const DocsTable = ({
  docs, emptyLabel, onDelete,
}: { docs: DocItem[]; emptyLabel: string; onDelete?: (doc: DocItem) => void }) => {
  const { t } = useTranslation();
  if (docs.length === 0) {
    return <div className="p-4 text-sm text-[#5A6B7A] italic">{emptyLabel}</div>;
  }
  const openDoc = async (doc: DocItem) => {
    const isFullUrl = /^https?:\/\//i.test(doc.file_url);
    const path = isFullUrl ? extractStoragePath(doc.file_url, doc.bucket) : doc.file_url;
    if (!path) { window.open(doc.file_url, '_blank'); return; }
    const { data: signed } = await supabase.storage.from(doc.bucket).createSignedUrl(path, 600);
    if (signed?.signedUrl) { window.open(signed.signedUrl, '_blank'); return; }
    const { data: pub } = supabase.storage.from(doc.bucket).getPublicUrl(path);
    if (pub?.publicUrl) { window.open(pub.publicUrl, '_blank'); return; }
    toast.error(t('adminDocuments.openError'));
  };
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('adminDocuments.tableNature')}</TableHead>
          <TableHead>{t('adminDocuments.tableFile')}</TableHead>
          <TableHead>{t('adminDocuments.tableStatus')}</TableHead>
          <TableHead>{t('adminDocuments.tableSize')}</TableHead>
          <TableHead>{t('adminDocuments.tableDate')}</TableHead>
          <TableHead className="text-right">{t('adminDocuments.tableAction')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {docs.map((d) => (
          <TableRow key={d.id}>
            <TableCell>
              <Badge className="bg-[#0880AE]/10 text-[#0880AE] border-[#0880AE]/20">
                {t(`adminDocuments.categories.${d.category}`, { defaultValue: t('adminDocuments.categories.other') })}
              </Badge>
            </TableCell>
            <TableCell className="max-w-xs truncate" title={d.title || d.file_name}>
              <div className="truncate">{d.title || d.file_name}</div>
              {d.addedByVelox && (
                <div className="text-xs text-[#5A6B7A]">{t('adminDocuments.addedByVelox')}</div>
              )}
            </TableCell>
            <TableCell><StatusBadge status={d.status} /></TableCell>
            <TableCell className="text-sm">{formatSize(d.file_size)}</TableCell>
            <TableCell className="text-sm">{format(new Date(d.created_at), 'dd/MM/yyyy HH:mm')}</TableCell>
            <TableCell className="text-right">
              <Button variant="ghost" size="sm" onClick={() => openDoc(d)}>
                <ExternalLink size={16} className="text-[#0880AE]" />
              </Button>
              {onDelete && d.companyDoc && (
                <Button variant="ghost" size="sm" onClick={() => onDelete(d)}>
                  <Trash2 size={16} className="text-[#FF7171]" />
                </Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const ResourceList = ({
  icon: Icon,
  items,
  emptyLabel,
  isDriver = false,
  onAdd,
  onDelete,
}: {
  icon: any;
  items: CompanyNode['fleet']['trucks'];
  emptyLabel: string;
  isDriver?: boolean;
  onAdd?: (id: string) => void;
  onDelete?: (doc: DocItem) => void;
}) => {
  const { t } = useTranslation();
  if (items.length === 0) return <div className="p-4 text-sm text-[#5A6B7A] italic">{emptyLabel}</div>;
  return (
    <div className="space-y-3">
      {items.map((it: any) => {
        const driverMeta = isDriver
          ? (it._license
              ? `${t('adminDocuments.licensePrefix')} ${it._license}${it._expiry ? ` · ${t('adminDocuments.licenseExpires')} ${format(new Date(it._expiry), 'dd/MM/yyyy')}` : ''}`
              : t('adminDocuments.licenseNotSet'))
          : it.meta;
        return (
          <div key={it.id} className="border border-[#DBE2EA] rounded-xl overflow-hidden">
            <div className="bg-[#F8F9FA] p-4 border-b border-[#DBE2EA] flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#0880AE]/10 flex items-center justify-center">
                  <Icon className="text-[#0880AE]" size={18} />
                </div>
                <div>
                  <div className="font-semibold text-[#1A202C] font-['Lexend_Deca']">{it.label}</div>
                  <div className="text-xs text-[#5A6B7A]">{driverMeta}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={it.status} />
                {onAdd && (
                  <Button variant="ghost" size="sm" onClick={() => onAdd(it.id)} className="text-[#0880AE]">
                    <Plus size={16} className="mr-1" /> {t('adminDocuments.add.buttonShort')}
                  </Button>
                )}
              </div>
            </div>
            <DocsTable docs={it.docs} emptyLabel={t('adminDocuments.noDocsResource')} onDelete={onDelete} />
          </div>
        );
      })}
    </div>
  );
};

const AdminDocuments = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: nodes = [], isLoading } = useAdminDocsTree();
  const [side, setSide] = useState<Side>('shipper');
  const [selected, setSelected] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [section, setSection] = useState<'onboarding' | 'fleet' | 'freight'>('onboarding');
  const [fleetTab, setFleetTab] = useState<'trucks' | 'trailers' | 'drivers'>('trucks');

  // document-level filters (inside a company)
  const [docSearch, setDocSearch] = useState('');
  const [docCategory, setDocCategory] = useState<string>('all');
  const [docStatus, setDocStatus] = useState<StatusFilter>('all');

  const [addOpen, setAddOpen] = useState(false);
  const [addOrderId, setAddOrderId] = useState<string | null>(null);
  const [addVehicleId, setAddVehicleId] = useState<string | null>(null);
  const [addDriverId, setAddDriverId] = useState<string | null>(null);
  const [docToDelete, setDocToDelete] = useState<DocItem | null>(null);

  const filtered = useMemo(() => {
    const list = nodes.filter((n) => n.side === side);
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(
      (n) =>
        n.company_name.toLowerCase().includes(q) ||
        n.users.some((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
    );
  }, [nodes, side, search]);

  const current = selected ? nodes.find((n) => n.company_id === selected) || null : null;

  const filtersActive = docSearch.trim() !== '' || docCategory !== 'all' || docStatus !== 'all';

  const matchDoc = (d: DocItem, extraText = '') => {
    if (docCategory !== 'all' && d.category !== docCategory) return false;
    if (docStatus !== 'all' && d.status !== docStatus) return false;
    if (docSearch.trim()) {
      const q = docSearch.toLowerCase();
      const hay = [
        d.file_name, d.title ?? '', d.notes ?? '', extraText,
        t(`adminDocuments.categories.${d.category}`, { defaultValue: '' }) as string,
      ].join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  };

  const resetFilters = () => {
    setDocSearch('');
    setDocCategory('all');
    setDocStatus('all');
  };

  const filteredOnboarding = useMemo(
    () => (current ? current.onboarding.filter((d) => matchDoc(d)) : []),
    [current, docSearch, docCategory, docStatus]
  );

  const filterResources = (items: CompanyNode['fleet']['trucks']) =>
    items
      .map((it: any) => ({ ...it, docs: it.docs.filter((d: DocItem) => matchDoc(d, `${it.label} ${it.meta}`)) }))
      .filter((it: any) => {
        if (!filtersActive) return true;
        if (it.docs.length > 0) return true;
        if (docCategory !== 'all') return false;
        const textOk = !docSearch.trim() || `${it.label} ${it.meta}`.toLowerCase().includes(docSearch.toLowerCase());
        const statusOk = docStatus === 'all' || it.status === docStatus;
        return textOk && statusOk;
      });

  const filteredFreight = useMemo(() => {
    if (!current) return [];
    return current.freight
      .map((f) => ({ ...f, docs: f.docs.filter((d) => matchDoc(d, f.order_number)) }))
      .filter((f) => (filtersActive ? f.docs.length > 0 : true))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [current, docSearch, docCategory, docStatus]);

  const handleSideChange = (v: string) => {
    setSide(v as Side);
    setSelected(null);
    setSection('onboarding');
  };

  const handleSelect = (id: string, nodeSide: Side) => {
    setSelected(id);
    resetFilters();
    setSection(nodeSide === 'transporter' ? 'fleet' : 'freight');
  };

  const confirmDelete = async () => {
    if (!docToDelete?.companyDoc) return;
    const doc = docToDelete.companyDoc;
    const { error } = await supabase.from('company_documents').delete().eq('id', doc.id);
    if (error) {
      toast.error(t('adminDocuments.add.deleteError'));
      return;
    }
    await supabase.storage.from(COMPANY_DOCS_BUCKET).remove([doc.file_url]);
    toast.success(t('adminDocuments.add.deleteSuccess'));
    setDocToDelete(null);
    queryClient.invalidateQueries({ queryKey: ['admin-docs-tree-v3'] });
    queryClient.invalidateQueries({ queryKey: ['company-documents'] });
  };

  const openAdd = (orderId: string | null) => {
    setAddOrderId(orderId);
    setAddVehicleId(null);
    setAddDriverId(null);
    setAddOpen(true);
  };

  const openAddForResource = (kind: 'vehicle' | 'driver', id: string) => {
    setAddOrderId(null);
    setAddVehicleId(kind === 'vehicle' ? id : null);
    setAddDriverId(kind === 'driver' ? id : null);
    setAddOpen(true);
  };

  const FilterBar = () => (
    <Card className="p-4 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="relative flex-1">
          <SearchInput
            placeholder={t('adminDocuments.docSearchPlaceholder')}
            value={docSearch}
            onChange={(e) => setDocSearch(e.target.value)}
          />
        </div>
        <Select value={docCategory} onValueChange={setDocCategory}>
          <SelectTrigger className="w-full lg:w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('adminDocuments.categoryAll')}</SelectItem>
            {COMPANY_DOC_CATEGORIES.map((c) => (
              <SelectItem key={c} value={c}>{t(`adminDocuments.categories.${c}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={docStatus} onValueChange={(v) => setDocStatus(v as StatusFilter)}>
          <SelectTrigger className="w-full lg:w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('adminDocuments.statusAll')}</SelectItem>
            <SelectItem value="verified">{t('adminDocuments.statusVerified')}</SelectItem>
            <SelectItem value="expiring">{t('adminDocuments.statusExpiring')}</SelectItem>
            <SelectItem value="expired">{t('adminDocuments.statusExpired')}</SelectItem>
            <SelectItem value="unverified">{t('adminDocuments.statusUnverified')}</SelectItem>
          </SelectContent>
        </Select>
        {filtersActive && (
          <Button variant="ghost" onClick={resetFilters} className="text-[#0880AE]">
            <RotateCcw size={16} className="mr-2" /> {t('adminDocuments.resetFilters')}
          </Button>
        )}
      </div>
    </Card>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-4 flex items-center gap-2 text-sm text-[#5A6B7A] font-['Funnel_Sans']">
          <button onClick={() => setSelected(null)} className="hover:text-[#0880AE] transition-colors">
            {t('adminDocuments.breadcrumb')}
          </button>
          <ChevronRight size={14} />
          <button onClick={() => setSelected(null)} className="hover:text-[#0880AE] transition-colors">
            {side === 'shipper' ? t('adminDocuments.shippers') : t('adminDocuments.transporters')}
          </button>
          {current && (
            <>
              <ChevronRight size={14} />
              <span className="text-[#1A202C] font-medium">{current.company_name}</span>
            </>
          )}
        </div>

        {!current ? (
          <>
            <div className="mb-6">
              <h1 className="text-3xl font-semibold text-[#37699E] font-['Lexend_Deca']">{t('adminDocuments.title')}</h1>
              <p className="text-sm text-[#5A6B7A] mt-2 font-['Funnel_Sans']">{t('adminDocuments.subtitle')}</p>
            </div>

            <Tabs value={side} onValueChange={handleSideChange}>
              <TabsList className="mb-6">
                <TabsTrigger value="shipper">
                  📦 {t('adminDocuments.shippers')} ({nodes.filter((n) => n.side === 'shipper').length})
                </TabsTrigger>
                <TabsTrigger value="transporter">
                  🚛 {t('adminDocuments.transporters')} ({nodes.filter((n) => n.side === 'transporter').length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value={side}>
                <Card className="p-6">
                  <div className="relative mb-3">
                    <SearchInput
                      placeholder={t('adminDocuments.searchPlaceholder')}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  <p className="text-xs text-[#5A6B7A] mb-6 font-['Funnel_Sans']">
                    {t('adminDocuments.companiesShown', { count: filtered.length })}
                  </p>

                  {isLoading ? (
                    <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-20" />)}</div>
                  ) : filtered.length === 0 ? (
                    <div className="text-center py-12 text-[#5A6B7A]">
                      <FileText className="mx-auto mb-3" size={40} />
                      <p>{t('adminDocuments.noCompanies', { side: t(side === 'shipper' ? 'adminDocuments.sideShipper' : 'adminDocuments.sideTransporter') })}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {filtered.map((n) => (
                        <button
                          key={n.company_id}
                          onClick={() => handleSelect(n.company_id, n.side)}
                          className="text-left border border-[#DBE2EA] hover:border-[#0880AE] rounded-xl p-4 bg-white hover:shadow-md transition-all duration-200"
                        >
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#37699E]/10 flex items-center justify-center">
                                <Building2 className="text-[#37699E]" size={20} />
                              </div>
                              <div>
                                <div className="font-semibold text-[#1A202C] font-['Lexend_Deca']">{n.company_name}</div>
                                <div className="text-xs text-[#5A6B7A]">{t('adminDocuments.usersCount', { count: n.users.length })}</div>
                              </div>
                            </div>
                            <ChevronRight className="text-[#87ADD4]" size={20} />
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge className="bg-[#0880AE]/10 text-[#0880AE] border-[#0880AE]/20">
                              {t('adminDocuments.docsCount', { count: n.totals.docs })}
                            </Badge>
                            {n.totals.expired > 0 && (
                              <Badge className="bg-[#FF7171]/10 text-[#FF7171] border-[#FF7171]/20">
                                <AlertTriangle size={12} className="mr-1" /> {t('adminDocuments.expiredCount', { count: n.totals.expired })}
                              </Badge>
                            )}
                            {n.totals.missing > 0 && (
                              <Badge className="bg-[#F2AC57]/10 text-[#F2AC57] border-[#F2AC57]/20">
                                <AlertTriangle size={12} className="mr-1" /> {t('adminDocuments.missingCount', { count: n.totals.missing })}
                              </Badge>
                            )}
                            {n.totals.expired === 0 && n.totals.missing === 0 && n.totals.docs > 0 && (
                              <Badge className="bg-[#14A38B]/10 text-[#14A38B] border-[#14A38B]/20">
                                <CheckCircle2 size={12} className="mr-1" /> {t('adminDocuments.complete')}
                              </Badge>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </Card>
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <>
            <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
              <div>
                <Button variant="ghost" size="sm" onClick={() => setSelected(null)} className="mb-2 -ml-2">
                  <ArrowLeft size={16} className="mr-1" /> {t('adminDocuments.back')}
                </Button>
                <h1 className="text-3xl font-semibold text-[#37699E] font-['Lexend_Deca']">{current.company_name}</h1>
                <p className="text-sm text-[#5A6B7A] mt-1 font-['Funnel_Sans']">
                  {t('adminDocuments.usersCount', { count: current.users.length })} · {t('adminDocuments.docsCount', { count: current.totals.docs })}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {current.totals.expired > 0 && (
                  <Badge className="bg-[#FF7171]/10 text-[#FF7171] border-[#FF7171]/20">
                    <AlertTriangle size={12} className="mr-1" /> {t('adminDocuments.expiredCount', { count: current.totals.expired })}
                  </Badge>
                )}
                {current.totals.missing > 0 && (
                  <Badge className="bg-[#F2AC57]/10 text-[#F2AC57] border-[#F2AC57]/20">
                    <AlertTriangle size={12} className="mr-1" /> {t('adminDocuments.missingCount', { count: current.totals.missing })}
                  </Badge>
                )}
                <Button
                  onClick={() => openAdd(null)}
                  className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold"
                >
                  <Plus size={16} className="mr-2" /> {t('adminDocuments.add.button')}
                </Button>
              </div>
            </div>

            <FilterBar />

            <Tabs value={section} onValueChange={(v) => setSection(v as any)}>
              <TabsList className="mb-6">
                <TabsTrigger value="onboarding">📋 {t('adminDocuments.sectionOnboarding')}</TabsTrigger>
                {current.side === 'transporter' && <TabsTrigger value="fleet">🚛 {t('adminDocuments.sectionFleet')}</TabsTrigger>}
                <TabsTrigger value="freight">📦 {t('adminDocuments.sectionFreight')}</TabsTrigger>
              </TabsList>

              <TabsContent value="onboarding">
                <Card className="p-6">
                  <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                    <div>
                      <h2 className="text-xl font-medium text-[#37699E] font-['Lexend_Deca'] mb-1">{t('adminDocuments.onboardingTitle')}</h2>
                      <p className="text-sm text-[#5A6B7A] font-['Funnel_Sans']">{t('adminDocuments.onboardingDesc')}</p>
                    </div>
                    <Button variant="outline" onClick={() => openAdd(null)} className="border-[#37699E] text-[#37699E]">
                      <Plus size={16} className="mr-2" /> {t('adminDocuments.add.button')}
                    </Button>
                  </div>
                  <DocsTable
                    docs={filteredOnboarding}
                    emptyLabel={filtersActive ? t('adminDocuments.noResults') : t('adminDocuments.onboardingEmpty')}
                    onDelete={setDocToDelete}
                  />
                </Card>
              </TabsContent>

              {current.side === 'transporter' && (
                <TabsContent value="fleet">
                  <Card className="p-6">
                    <h2 className="text-xl font-medium text-[#37699E] font-['Lexend_Deca'] mb-4">{t('adminDocuments.fleetTitle')}</h2>
                    <Tabs value={fleetTab} onValueChange={(v) => setFleetTab(v as any)}>
                      <TabsList className="mb-4">
                        <TabsTrigger value="trucks">
                          <Truck size={14} className="mr-1" /> {t('adminDocuments.trucks')} ({current.fleet.trucks.length})
                        </TabsTrigger>
                        <TabsTrigger value="trailers">
                          <Container size={14} className="mr-1" /> {t('adminDocuments.trailers')} ({current.fleet.trailers.length})
                        </TabsTrigger>
                        <TabsTrigger value="drivers">
                          <User size={14} className="mr-1" /> {t('adminDocuments.drivers')} ({current.fleet.drivers.length})
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="trucks">
                        <ResourceList
                          icon={Truck}
                          items={filterResources(current.fleet.trucks)}
                          emptyLabel={filtersActive ? t('adminDocuments.noResults') : t('adminDocuments.emptyTrucks')}
                          onAdd={(id) => openAddForResource('vehicle', id)}
                          onDelete={setDocToDelete}
                        />
                      </TabsContent>
                      <TabsContent value="trailers">
                        <ResourceList
                          icon={Container}
                          items={filterResources(current.fleet.trailers)}
                          emptyLabel={filtersActive ? t('adminDocuments.noResults') : t('adminDocuments.emptyTrailers')}
                          onAdd={(id) => openAddForResource('vehicle', id)}
                          onDelete={setDocToDelete}
                        />
                      </TabsContent>
                      <TabsContent value="drivers">
                        <ResourceList
                          icon={User}
                          items={filterResources(current.fleet.drivers)}
                          emptyLabel={filtersActive ? t('adminDocuments.noResults') : t('adminDocuments.emptyDrivers')}
                          isDriver
                          onAdd={(id) => openAddForResource('driver', id)}
                          onDelete={setDocToDelete}
                        />
                      </TabsContent>
                    </Tabs>
                  </Card>
                </TabsContent>
              )}

              <TabsContent value="freight">
                <Card className="p-6">
                  <h2 className="text-xl font-medium text-[#37699E] font-['Lexend_Deca'] mb-1">{t('adminDocuments.freightTitle')}</h2>
                  <p className="text-sm text-[#5A6B7A] mb-4 font-['Funnel_Sans']">{t('adminDocuments.freightDesc')}</p>
                  {filteredFreight.length === 0 ? (
                    <div className="p-4 text-sm text-[#5A6B7A] italic">
                      {filtersActive ? t('adminDocuments.noResults') : t('adminDocuments.freightEmpty')}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredFreight.map((f) => (
                        <div key={f.order_id} className="border border-[#DBE2EA] rounded-xl overflow-hidden">
                          <div className="bg-[#F8F9FA] p-4 border-b border-[#DBE2EA] flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-lg bg-[#FFBF07]/20 flex items-center justify-center">
                                <Package className="text-[#CC9805]" size={18} />
                              </div>
                              <div>
                                <div className="font-semibold text-[#1A202C] font-['Lexend_Deca']">
                                  {t('adminDocuments.orderLabel', { number: f.order_number })}
                                </div>
                                <div className="text-xs text-[#5A6B7A]">{format(new Date(f.created_at), 'dd/MM/yyyy')}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className="bg-[#0880AE]/10 text-[#0880AE] border-[#0880AE]/20">
                                {t('adminDocuments.docsCount', { count: f.docs.length })}
                              </Badge>
                              <Button variant="ghost" size="sm" onClick={() => openAdd(f.order_id)} className="text-[#0880AE]">
                                <Plus size={16} className="mr-1" /> {t('adminDocuments.add.buttonShort')}
                              </Button>
                            </div>
                          </div>
                          <DocsTable docs={f.docs} emptyLabel={t('adminDocuments.noDocs')} onDelete={setDocToDelete} />
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </TabsContent>
            </Tabs>

            <AddCompanyDocumentDialog
              open={addOpen}
              onOpenChange={(v) => {
                setAddOpen(v);
                if (!v) queryClient.invalidateQueries({ queryKey: ['admin-docs-tree-v3'] });
              }}
              companyId={current.company_id}
              companyName={current.company_name}
              orders={current.freight.map((f) => ({ order_id: f.order_id, order_number: f.order_number }))}
              vehicles={current.side === 'transporter'
                ? [...current.fleet.trucks, ...current.fleet.trailers].map((v) => ({
                    id: v.id,
                    label: v.meta && v.meta !== '—' ? `${v.label} · ${v.meta}` : v.label,
                  }))
                : []}
              isTransporter={current.side === 'transporter'}
              drivers={current.side === 'transporter'
                ? current.fleet.drivers.map((d) => ({ id: d.id, label: d.label }))
                : []}
              defaultOrderId={addOrderId}
              defaultVehicleId={addVehicleId}
              defaultDriverId={addDriverId}
            />

            <AlertDialog open={!!docToDelete} onOpenChange={(v) => !v && setDocToDelete(null)}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('adminDocuments.add.deleteTitle')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('adminDocuments.add.deleteDescription', { name: docToDelete?.file_name || '' })}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={confirmDelete} className="bg-[#FF7171] hover:bg-[#E65959]">
                    {t('common.delete')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDocuments;
